/**
 * People research: one search across the public-source providers, and what it found.
 *
 * **Nothing on this page claims a result is the person.** Every candidate shows the
 * arithmetic that made it one — which attributes matched, what each was worth, against what
 * text — and the strongest thing it will say is *possible match*. That is not a UI
 * preference: a page cannot confirm an identity, and a label that said otherwise would be
 * the interface making the judgement the analyst is there to make (ADR-0038).
 *
 * **The case is optional; the accountability is not.** A search either attaches to a case
 * and inherits its attestation, or states its own purpose, legal basis and authorisation
 * and opens a research session (ADR-0039). What is refused, here as at the endpoint, is a
 * search recording neither — that would be the way around the gate.
 *
 * This comment used to say the opposite: *"the search runs inside a case, chosen before
 * searching… a form that opened its own case would be a way around the gate."* That was
 * true until the endpoint accepted the second shape, and then stayed on the page, in the
 * `disabled` on the Search button, and in a test that asserted it — so the release whose
 * headline was "research a person without opening a case" shipped with the case still
 * mandatory in the only interface anybody uses. Neither guard caught it: surface coverage
 * asks whether an *endpoint* is reachable, and this one always was.
 *
 * **What the sources see is public.** The providers ask the deployment's own SearXNG
 * instance what is already indexed; nothing here reads private or authenticated content on
 * LinkedIn, Facebook or Instagram, and the page says so where a researcher will read it.
 */

import { useState, type FormEvent, type ReactElement } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { LEGAL_BASES, PURPOSES } from "@/lib/attestation";
import { Badge, Button, ErrorState, Loading } from "@/components/ui";
import { useCan } from "@/hooks/usePermissions";
import type { SubjectRow } from "@/features/people/SubjectWorkspace";
import type {
  AttachEvidenceRequest,
  PeopleSearchRequest,
  SaveSubjectRequest,
  SearchLegalBasis,
  SearchPurpose,
} from '@/lib/schemas';

interface CaseRow {
  id: string;
  reference: string;
  title: string;
}

interface SearchSummary {
  run_id: string;
  record_id: string;
  title: string;
  status: string;
  created_at: string;
}

interface Search {
  run_id: string;
  case_id: string;
  status: string;
  providers: string[];
  skipped: Record<string, string>;
  task_count: number;
  task_states: Record<string, number>;
  stalled: boolean;
}

/** Run statuses that will not change again — a Stop offered on one would do nothing. */
const TERMINAL_STATUS = new Set(["complete", "completed_partial", "failed", "cancelled"]);

interface MatchFeature {
  name: string;
  weight: number;
  detail: string;
}

interface Evidence {
  observation_id: string;
  source: string;
  status: string;
  collected_at: string;
  source_url: string;
  evidence_sha256: string | null;
  error_class: string | null;
}

interface AttachedSubject {
  id: string;
  display_name: string;
}

interface Result {
  assertion_id: string;
  source: string;
  predicate: string;
  object_value: string;
  title: string;
  snippet: string;
  engine: string;
  result_type: string;
  evidence_type: string | null;
  evidence_url: string | null;
  discovered_at: string;
  evidence_sha256: string | null;
  match_score: number | null;
  match_verdict: string | null;
  match_features: MatchFeature[];
  // Saved subjects this result is already attached to. The list shows these rather than
  // offering an attach that would be a no-op the server now absorbs.
  attached_subjects: AttachedSubject[];
}

const FIELDS = [
  { key: "name", label: "Name" },
  { key: "organisation", label: "Organization" },
  { key: "job_title", label: "Job title" },
  { key: "location", label: "Location" },
  { key: "username", label: "Username / handle" },
] as const;

type FieldKey = (typeof FIELDS)[number]["key"];

/** How a verdict is worded and toned. There is no `confirmed`. */
const VERDICTS: Record<
  string,
  { label: string; tone: "ok" | "warn" | "muted" }
> = {
  possible_match: { label: "Possible match", tone: "ok" },
  weak_match: { label: "Weak match", tone: "warn" },
  no_corroboration: { label: "No corroboration", tone: "muted" },
};

/**
 * The People Research screen.
 *
 * @returns The page.
 */
export function PeopleResearchPage(): ReactElement {
  const canSearch = useCan("create_run_cases");
  const queryClient = useQueryClient();

  const [caseId, setCaseId] = useState("");
  // **Kept when a case is chosen rather than cleared.** Switching to a case omits these
  // from the payload, because the endpoint refuses both shapes at once — but a researcher
  // who selects a case, reconsiders and switches back should find their attestation still
  // typed. Discarding it silently is the same defect as sending it silently.
  const [session, setSession] = useState<{
    investigation_purpose: SearchPurpose;
    legal_basis: SearchLegalBasis;
    authorization_attestation: string;
  }>({
    investigation_purpose: "",
    legal_basis: "",
    authorization_attestation: "",
  });
  const [fields, setFields] = useState<Record<FieldKey, string>>({
    name: "",
    organisation: "",
    job_title: "",
    location: "",
    username: "",
  });
  // **The run is in the address, not in this component's memory.** It was `useState`, so
  // changing page lost the only handle to results that were already in the database — and
  // a research session never appears in the case list, by design, so nothing else could
  // reach them either. Re-running the search was the only way back, which spends egress
  // and rate limit re-collecting evidence the platform already holds.
  //
  // The subject workspace was built this way deliberately — "a nav over sub-routes, one
  // per panel — so every panel has an address a researcher can bookmark, share in a
  // ticket, or return to". This screen simply never got the same treatment.
  const { runId = "" } = useParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{
    source: string;
    result_type: string;
    verdict: string;
  }>({
    source: "",
    result_type: "",
    verdict: "",
  });

  // The saved-subject list is shared across the workspace, so deleting one removes it from
  // every colleague's view and destroys their notes with it. Same authority as deleting a
  // case, for the reason ADR-0042 gives: authority follows who the change affects.
  const canDelete = useCan("delete_cases");
  const remove = useMutation({
    mutationFn: (subjectId: string) =>
      request<{ deleted: boolean }>(`/research/subjects/${subjectId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["research-subjects"] });
    },
  });

  const cases = useQuery({
    queryKey: ["cases"],
    queryFn: () => request<{ data: CaseRow[] }>("/cases?limit=50"),
  });

  // **The independent sessions this workspace runs, listed so their results survive.** A
  // session never appears in the case list (ADR-0039), and a run was reachable only by the
  // URL in the browser — so a result nobody attached during the session became unreachable
  // the moment the researcher left the page. This is the handle back to it: the literal
  // ends at `/searches` so the surface-coverage check sees the endpoint as reachable.
  const searches = useQuery({
    queryKey: ["people-searches"],
    queryFn: () => request<SearchSummary[]>("/people-research/searches"),
  });

  // The mode is the case selector's own state, not a separate toggle. A second control
  // saying which of two things the first control already said is a control that can
  // disagree with it.
  const independent = caseId === "";
  const attested =
    session.investigation_purpose !== "" &&
    session.legal_basis !== "" &&
    session.authorization_attestation.trim() !== "";
  const ready = independent ? attested : true;

  const search = useMutation({
    mutationFn: () =>
      // `body` is the payload, not its encoding: `request` serialises it. Stringifying
      // here too sent a JSON *string* where the endpoint expects an object, and every
      // search failed validation before it reached the pipeline.
      // **One shape or the other, never a blend.** The endpoint declares `extra="forbid"`
      // and refuses a request naming a case *and* a purpose, so the attestation keys are
      // absent in case mode rather than sent empty. `case_id` is likewise omitted
      // entirely for a session: the field is `UUID | None`, and `""` is not absence — it
      // is a 422, which is what this page sent for every search that named no case.
      request<Search, PeopleSearchRequest>("/people-research/searches", {
        method: "POST",
        body: independent
          ? { ...session, ...fields }
          : { case_id: caseId, ...fields },
      }),
    onSuccess: (created) => {
      // Replace rather than push: the empty search form and the search it produced are
      // one step in the history, so Back leaves People Research instead of returning to a
      // form whose results are one keystroke away.
      navigate(`/people/searches/${created.run_id}`, { replace: true });
      void queryClient.invalidateQueries({ queryKey: ["people-search"] });
      void queryClient.invalidateQueries({ queryKey: ["people-searches"] });
    },
  });

  // **Stop a run that is still working.** The same cancel the Runs tab offers, reached from
  // where a researcher actually meets a stuck search: it settles the run and marks the
  // sources that never ran as skipped, so a session frozen on "collecting" resolves at once
  // rather than waiting for the sweep.
  const stop = useMutation({
    mutationFn: (id: string) => request<Search>(`/runs/${id}:cancel`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["people-search"] });
      void queryClient.invalidateQueries({ queryKey: ["people-searches"] });
    },
  });

  // **Remove the session from the list.** Soft-deletes the session's record, so it and its
  // runs drop out of every listing while the evidence and the audit chain remain. Not
  // erasure — that removes what was collected about a person, and is a different control.
  const removeSearch = useMutation({
    mutationFn: (id: string) =>
      request<null>(`/people-research/searches/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["people-searches"] });
    },
  });

  // Polled while collection is in flight: a run is accepted, not finished, and the results
  // arrive as the providers answer.
  const status = useQuery({
    queryKey: ["people-search", runId],
    queryFn: () => request<Search>(`/people-research/searches/${runId}`),
    enabled: runId !== "",
    refetchInterval: (query) =>
      ["complete", "failed", "partial", "awaiting_review"].includes(
        query.state.data?.status ?? "",
      )
        ? false
        : 3000,
  });

  const results = useQuery({
    queryKey: ["people-search", runId, "results", filters],
    queryFn: () => {
      const params = new URLSearchParams(
        Object.entries(filters).filter(([, value]) => value !== ""),
      );
      // The path literal ends at `/results` on purpose: the surface-coverage check reads
      // these call sites to prove every endpoint is reachable from a screen, and a path
      // interpolated at its tail matches nothing it publishes.
      const path = `/people-research/searches/${runId}/results`;
      const query = params.toString();
      return request<Result[]>(query === "" ? path : `${path}?${query}`);
    },
    enabled: runId !== "",
    refetchInterval: status.data?.status === "collecting" ? 3000 : false,
  });

  // Fetched so a researcher can see what was captured, and check a result against the
  // stored bytes rather than against this page's rendering of them.
  const evidence = useQuery({
    queryKey: ["people-search", runId, "evidence"],
    queryFn: () =>
      request<Evidence[]>(`/people-research/searches/${runId}/evidence`),
    enabled: runId !== "",
  });

  // Saved subjects are not scoped to this search or to a case — that is the point of
  // `research_subject`. The list is the same one the workspace reads, under the same key,
  // so opening a subject after saving it does not refetch.
  const subjects = useQuery({
    queryKey: ["research-subjects"],
    queryFn: () => request<SubjectRow[]>("/research/subjects?limit=200"),
  });

  // **The subject is the person searched for, not a result row.** Every candidate is a
  // page that may be about them; the thing worth keeping is the person the attributes
  // describe, which is exactly what the save endpoint takes and what makes saving the
  // same person twice idempotent rather than a duplicate.
  const save = useMutation({
    mutationFn: () =>
      request<SubjectRow, SaveSubjectRequest>("/research/subjects", { method: "POST", body: fields }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["research-subjects"] });
    },
  });

  // **Attaching is what makes the subject worth having.** Saving a person records that
  // they matter; attaching the pages that persuaded you is the research. Without this the
  // evidence panel could only ever be empty, and a detach with no attach is a feature
  // nobody can reach — the shape the surface-coverage backlog exists to catch.
  const attach = useMutation({
    mutationFn: ({ subjectId, assertionId }: { subjectId: string; assertionId: string }) =>
      request<{ id: string }, AttachEvidenceRequest>(`/research/subjects/${subjectId}/evidence`, {
        method: "POST",
        body: { assertion_id: assertionId, rationale: "Attached from a search result." },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["research-subjects"] });
      // Refetch the results too: their `attached_subjects` is what draws the "already
      // attached" badge, so it has to catch up with the attach just made.
      void queryClient.invalidateQueries({ queryKey: ["people-search", runId, "results"] });
    },
  });

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    // Guarded on the same condition the button is disabled by, not on `caseId` — this
    // line said `caseId !== ""` and was the third copy of the old rule, after the
    // `disabled` and the page comment. A submit path that disagrees with the button is
    // the one a keyboard user finds.
    if (ready) search.mutate();
  };

  const rows = results.data ?? [];
  const interests = rows.filter((row) => row.predicate === "has_interest");
  const pages = rows.filter((row) => row.predicate !== "has_interest");
  const sources = [...new Set(rows.map((row) => row.source))].sort();

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold">People research</h1>
        <p className="max-w-3xl text-xs text-muted">
          Searches public sources for pages that may belong to a person. Results
          are candidates, never confirmations — the score shows which attributes
          matched and what each was worth, and you decide. Social sources return
          only what a search engine has publicly indexed; nothing here reads
          private or logged-in content.
        </p>
      </header>

      <section className="space-y-2" aria-label="Saved subjects">
        <h2 className="text-sm font-semibold">Saved subjects</h2>
        <p className="max-w-3xl text-xs text-muted">
          People a researcher chose to keep. A subject belongs to no case and outlives any
          one search, so the same person found twice is one subject rather than two.
        </p>
        {subjects.isLoading && <Loading label="Loading subjects" />}
        {subjects.error != null && <ErrorState error={subjects.error} />}
        {subjects.data != null && subjects.data.length === 0 && (
          <p className="text-xs text-muted" data-testid="subjects-empty">
            None saved yet. Run a search and keep whoever is worth researching.
          </p>
        )}
        {subjects.data != null && subjects.data.length > 0 && (
          <ul className="space-y-1" data-testid="subject-list">
            {subjects.data.map((subject) => (
              <li key={subject.id} className="flex items-center gap-2">
                <Link
                  className="flex flex-wrap items-center gap-2 rounded border border-line p-2 text-sm hover:border-accent"
                  to={`/people/subjects/${subject.id}`}
                  data-testid="subject-link"
                >
                  <span className="font-semibold">{subject.display_name}</span>
                  <Badge tone="muted">{subject.status.replaceAll("_", " ")}</Badge>
                  <span className="text-xs text-muted">
                    {subject.evidence_count} piece(s) of evidence
                  </span>
                </Link>
                {canDelete && (
                  <button
                    type="button"
                    className="rounded border border-line px-2 py-1 text-xs text-muted hover:border-danger hover:text-danger"
                    data-testid="delete-subject"
                    disabled={remove.isPending}
                    title="Delete this subject and everything attached to it"
                    onClick={() => {
                      // Confirmed because it is the one withdrawal here that really
                      // deletes: evidence detaches keeping its link, a note is withdrawn,
                      // and this takes the notes and links with it — including colleagues'.
                      if (
                        window.confirm(
                          `Delete ${subject.display_name}? Its attached evidence, notes and case ` +
                            `cross-references go with it. This cannot be undone.`,
                        )
                      ) {
                        remove.mutate(subject.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2" aria-label="Recent searches">
        <h2 className="text-sm font-semibold">Recent searches</h2>
        <p className="max-w-3xl text-xs text-muted">
          Independent research sessions you have run. Reopen one to review its results and
          attach any you did not keep the first time. Searches attached to a case are
          reached from the case.
        </p>
        {searches.isLoading && <Loading label="Loading searches" />}
        {searches.error != null && <ErrorState error={searches.error} />}
        {searches.data != null && searches.data.length === 0 && (
          <p className="text-xs text-muted" data-testid="searches-empty">
            No independent sessions yet. A search run without a case appears here.
          </p>
        )}
        {searches.data != null && searches.data.length > 0 && (
          <ul className="space-y-1" data-testid="search-list">
            {searches.data.map((row) => (
              <li key={row.run_id} className="flex flex-wrap items-center gap-2">
                <Link
                  className="flex flex-1 flex-wrap items-center gap-2 rounded border border-line p-2 text-sm hover:border-accent"
                  to={`/people/searches/${row.run_id}`}
                  data-testid="search-link"
                >
                  <span className="font-semibold">{row.title}</span>
                  <Badge tone="muted">{row.status.replaceAll("_", " ")}</Badge>
                  <span className="text-xs text-muted">
                    {row.created_at.slice(0, 10)}
                  </span>
                </Link>
                {/* Stop only where it would do something: a settled run cannot be
                    cancelled, and offering it would return a 409 the researcher cannot
                    act on. */}
                {!TERMINAL_STATUS.has(row.status) && (
                  <button
                    type="button"
                    className="rounded border border-line px-2 py-1 text-xs text-muted hover:border-accent hover:text-accent disabled:opacity-50"
                    data-testid={`stop-search-${row.run_id}`}
                    disabled={stop.isPending}
                    title="Stop this search — settle the run and skip any source that has not run"
                    onClick={() => stop.mutate(row.run_id)}
                  >
                    Stop
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="rounded border border-line px-2 py-1 text-xs text-muted hover:border-danger hover:text-danger disabled:opacity-50"
                    data-testid={`delete-search-${row.run_id}`}
                    disabled={removeSearch.isPending}
                    title="Remove this session from the list; its evidence is kept"
                    onClick={() => {
                      // Confirmed, but gently: this hides the session and its runs, it does
                      // not erase what was collected — so the warning says what stays, not
                      // that it cannot be undone.
                      if (
                        window.confirm(
                          `Remove the research session “${row.title}”? It disappears from ` +
                            `this list. The evidence it collected is kept under the ` +
                            `retention policy.`,
                        )
                      ) {
                        removeSearch.mutate(row.run_id);
                      }
                    }}
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <form className="space-y-3" onSubmit={submit} aria-label="People search">
        <label className="block text-sm">
          Case
          <select
            className="mt-1 w-full rounded border border-line bg-surface p-2"
            data-testid="people-case"
            value={caseId}
            onChange={(event) => setCaseId(event.target.value)}
          >
            <option value="">No case — start an independent research session</option>
            {(cases.data?.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.reference} — {row.title}
              </option>
            ))}
          </select>
        </label>

        {independent && (
          <fieldset
            className="space-y-3 rounded border border-line p-3"
            data-testid="session-attestation"
          >
            <legend className="px-1 text-sm font-semibold">
              Independent research session
            </legend>
            <p className="max-w-3xl text-xs text-muted">
              No case selected — this will start an independent research session. It
              records the same three answers a case records, and belongs to no case. It is
              not added to any case, and no case is created.
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm">
                Investigation purpose
                <select
                  className="mt-1 w-full rounded border border-line bg-surface p-2"
                  data-testid="session-purpose"
                  value={session.investigation_purpose}
                  onChange={(event) =>
                    setSession((current) => ({
                      ...current,
                      investigation_purpose: event.target.value as SearchPurpose,
                    }))
                  }
                >
                  <option value="">Choose why this search is being run…</option>
                  {PURPOSES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-sm">
                Legal basis
                <select
                  className="mt-1 w-full rounded border border-line bg-surface p-2"
                  data-testid="session-legal-basis"
                  value={session.legal_basis}
                  onChange={(event) =>
                    setSession((current) => ({
                      ...current,
                      legal_basis: event.target.value as SearchLegalBasis,
                    }))
                  }
                >
                  <option value="">Choose the basis relied on…</option>
                  {LEGAL_BASES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block text-sm">
              Authorisation attestation
              <textarea
                className="mt-1 w-full rounded border border-line bg-surface p-2"
                data-testid="session-attestation-text"
                rows={3}
                maxLength={4000}
                value={session.authorization_attestation}
                onChange={(event) =>
                  setSession((current) => ({
                    ...current,
                    authorization_attestation: event.target.value,
                  }))
                }
              />
              <span className="mt-1 block text-xs text-muted">
                Who authorised this research, and under what engagement. Recorded against
                your name and this moment, and immutable afterwards.
              </span>
            </label>
          </fieldset>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {FIELDS.map((field) => (
            <label key={field.key} className="block text-sm">
              {field.label}
              <input
                className="mt-1 w-full rounded border border-line bg-surface p-2"
                data-testid={`people-${field.key}`}
                value={fields[field.key]}
                onChange={(event) =>
                  setFields((current) => ({
                    ...current,
                    [field.key]: event.target.value,
                  }))
                }
              />
            </label>
          ))}
        </div>

        {!canSearch ? (
          <p className="text-xs text-muted">
            Running a search needs the <code>create_run_cases</code> permission.
          </p>
        ) : (
          <div className="space-y-1">
            <Button
              data-testid="people-search"
              aria-describedby={ready ? undefined : "people-search-blocked"}
              disabled={!ready || search.isPending}
            >
              {search.isPending ? "Searching…" : "Search"}
            </Button>
            {!ready && (
              <p
                className="text-xs text-muted"
                id="people-search-blocked"
                data-testid="people-search-blocked"
              >
                Choose a case, or state a purpose, a legal basis and an authorisation to
                research without one.
              </p>
            )}
          </div>
        )}
        {search.error != null && <ErrorState error={search.error} />}
      </form>

      {status.data != null && (
        <section className="space-y-2" aria-label="Search status">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone="accent">{status.data.status}</Badge>
            <span className="text-muted">
              {status.data.providers.length} source(s) ran
            </span>
            {!TERMINAL_STATUS.has(status.data.status) && (
              <button
                type="button"
                className="rounded border border-line px-2 py-1 text-xs text-muted hover:border-accent hover:text-accent disabled:opacity-50"
                data-testid="stop-search"
                disabled={stop.isPending}
                title="Stop this search — settle the run and skip any source that has not run"
                onClick={() => stop.mutate(status.data.run_id)}
              >
                Stop
              </button>
            )}
          </div>
          {/* **Why zero, not just zero.** A search that ran no sources is otherwise a dead
              end a researcher cannot read: was it refused, is it still queued, or is it
              stuck? The task fan-out answers it — a pending task was queued and never
              dispatched, which past the timeout is a stall the sweep will settle. */}
          {status.data.providers.length === 0 &&
            Object.keys(status.data.skipped).length === 0 && (
              <p className="text-xs text-muted" data-testid="people-why-empty">
                {status.data.stalled
                  ? "Stalled: the sources were queued but never picked up by a worker. " +
                    "Stop it now, or the scheduled sweep will settle it shortly."
                  : (status.data.task_states.pending ?? 0) > 0
                    ? `${status.data.task_states.pending} source(s) queued, none dispatched yet.`
                    : (status.data.task_states.skipped ?? 0) > 0
                      ? "The sources were skipped before they ran — this search was stopped."
                      : "No source was eligible to run for this search."}
              </p>
            )}
          {Object.keys(status.data.skipped).length > 0 && (
            // A source that was refused and a source with nothing to say are different
            // facts, and only one of them is about the person.
            <ul className="text-xs text-muted" data-testid="people-skipped">
              {Object.entries(status.data.skipped).map(([source, reason]) => (
                <li key={source}>
                  <span className="font-mono">{source}</span> —{" "}
                  {reason.replaceAll("_", " ")}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {runId !== "" && (
        <section className="space-y-3" aria-label="Results">
          <div className="flex flex-wrap gap-2 text-xs">
            <select
              className="rounded border border-line bg-surface p-1"
              data-testid="filter-source"
              value={filters.source}
              onChange={(event) =>
                setFilters((f) => ({ ...f, source: event.target.value }))
              }
            >
              <option value="">Every source</option>
              {sources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
            <select
              className="rounded border border-line bg-surface p-1"
              data-testid="filter-verdict"
              value={filters.verdict}
              onChange={(event) =>
                setFilters((f) => ({ ...f, verdict: event.target.value }))
              }
            >
              <option value="">Every strength</option>
              {Object.entries(VERDICTS).map(([value, meta]) => (
                <option key={value} value={value}>
                  {meta.label}
                </option>
              ))}
            </select>
          </div>

          {results.isLoading && <Loading />}
          {results.error != null && <ErrorState error={results.error} />}

          {interests.length > 0 && (
            <div className="space-y-1" data-testid="people-interests">
              <h2 className="text-sm font-semibold">Stated interests</h2>
              <p className="text-xs text-muted">
                Terms that appear in the text a source returned, each linking to
                the page that stated it. Not inference: nothing here concludes
                anything about the person.
              </p>
              <ul className="flex flex-wrap gap-2">
                {interests.map((row) => (
                  <li key={row.assertion_id}>
                    <a
                      className="rounded border border-line px-2 py-1 text-xs hover:border-accent"
                      href={row.evidence_url ?? row.object_value}
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {row.object_value}
                      <span className="ml-1 text-muted">
                        ({(row.evidence_type ?? "").replaceAll("_", " ")})
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(evidence.data ?? []).length > 0 && (
            <details
              className="rounded border border-line p-3 text-xs"
              data-testid="people-evidence"
            >
              <summary className="cursor-pointer text-sm font-semibold">
                Evidence ({(evidence.data ?? []).length} capture(s))
              </summary>
              <ul className="mt-2 space-y-1 text-muted">
                {(evidence.data ?? []).map((capture) => (
                  <li key={capture.observation_id}>
                    <span className="font-mono">{capture.source}</span> —{" "}
                    {capture.status}
                    {capture.evidence_sha256 != null && (
                      <span className="ml-1 font-mono">
                        {capture.evidence_sha256.slice(0, 12)}…
                      </span>
                    )}
                    {capture.error_class != null && (
                      <span className="ml-1">({capture.error_class})</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">Candidates</h2>
              {canSearch && (
                <Button
                  tone="secondary"
                  data-testid="save-subject"
                  disabled={save.isPending}
                  onClick={() => save.mutate()}
                >
                  {save.isPending ? "Saving…" : "Save as subject"}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted">
              Saving keeps the person these attributes describe, not one of the pages
              below. Attaching the pages that persuaded you is done in the subject&apos;s
              workspace.
            </p>
            {save.error != null && <ErrorState error={save.error} />}
            {pages.length === 0 && !results.isLoading && (
              <p className="text-xs text-muted" data-testid="people-empty">
                Nothing yet. A search that collected nothing has no results —
                which is a finding, not an error.
              </p>
            )}
            {pages.map((row) => {
              const verdict = VERDICTS[row.match_verdict ?? ""] ?? {
                label: "Unscored",
                tone: "muted" as const,
              };
              return (
                <article
                  key={row.assertion_id}
                  className="space-y-1 rounded border border-line p-3"
                  data-testid="people-result"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={verdict.tone}>{verdict.label}</Badge>
                    {row.match_score != null && (
                      <span className="text-xs text-muted">
                        {row.match_score} / 100
                      </span>
                    )}
                    <Badge tone="muted">{row.result_type || "result"}</Badge>
                    <span className="font-mono text-xs text-muted">
                      {row.source}
                    </span>
                  </div>
                  <a
                    className="block text-sm text-accent"
                    href={row.object_value}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    {row.title || row.object_value}
                  </a>
                  <p className="text-xs text-muted">{row.snippet}</p>
                  {row.attached_subjects.length > 0 && (
                    // **State, not an action.** The person already attached this page to
                    // these subjects; showing it is what keeps them from attaching it a
                    // second time, and a result can belong to more than one subject.
                    <div
                      className="flex flex-wrap items-center gap-1 pt-1"
                      data-testid="attached-subjects"
                    >
                      <span className="text-xs text-muted">Attached to</span>
                      {row.attached_subjects.map((subject) => (
                        <Badge key={subject.id} tone="ok">
                          {subject.display_name}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {canSearch && (subjects.data ?? []).length > 0 && (() => {
                    const attachedIds = new Set(
                      row.attached_subjects.map((subject) => subject.id),
                    );
                    return (
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <label className="text-xs text-muted">
                        Attach to
                        <select
                          className="ml-1 rounded border border-line bg-surface p-1 text-xs"
                          data-testid="attach-subject"
                          defaultValue=""
                          onChange={(event) => {
                            if (event.target.value !== "") {
                              attach.mutate({
                                subjectId: event.target.value,
                                assertionId: row.assertion_id,
                              });
                              event.target.value = "";
                            }
                          }}
                        >
                          <option value="">a saved subject…</option>
                          {(subjects.data ?? []).map((subject) => {
                            // An already-attached subject stays visible but unpickable, so
                            // the control cannot fire an attach the server would absorb as
                            // a no-op — and the "— attached" says why it is greyed.
                            const isAttached = attachedIds.has(subject.id);
                            return (
                              <option
                                key={subject.id}
                                value={subject.id}
                                disabled={isAttached}
                              >
                                {subject.display_name}
                                {isAttached ? " — attached" : ""}
                              </option>
                            );
                          })}
                        </select>
                      </label>
                    </div>
                    );
                  })()}
                  {row.match_features.length > 0 && (
                    // **The arithmetic, not the total.** A score nobody can re-add is a
                    // request to trust a computation they cannot inspect.
                    <ul
                      className="text-xs text-muted"
                      data-testid="match-features"
                    >
                      {row.match_features.map((feature) => (
                        <li key={feature.name}>
                          <span className="font-mono">{feature.name}</span> +
                          {feature.weight} — {feature.detail}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
